import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, Command as CommandIcon, User, Calendar, Package, Receipt, Scissors,
  TrendingDown, Tag as TagIcon, ArrowRight, Zap, GitBranch, Mail, LayoutDashboard,
  CircleCheck, Folder, Users as UsersIcon, Store, GraduationCap, Megaphone,
  Building2, TrendingUp, Share2, Inbox as InboxIcon,
} from "lucide-react";

/**
 * CommandPalette — opens with Cmd/Ctrl+K.
 *
 * Context-aware: in `/admin/*` cerca solo elementi dell'agenzia
 * (task, progetti, contatti HQ, saloni, sezioni admin). In `/app/*`
 * cerca elementi del salone (clienti, servizi, prodotti, tag, sezioni tenant).
 */
export function CommandPalette() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [tenantIdx, setTenantIdx] = useState({ clients: [], products: [], services: [], tags: [] });
  const [adminIdx, setAdminIdx] = useState({ tasks: [], projects: [], contacts: [], tenants: [] });
  const [highlight, setHighlight] = useState(0);

  // Carica gli indici la prima volta che la palette si apre, in base al contesto.
  useEffect(() => {
    if (!open) return;
    if (isAdmin) {
      const already = adminIdx.tasks.length + adminIdx.projects.length + adminIdx.contacts.length + adminIdx.tenants.length;
      if (already > 0) return;
      Promise.all([
        api.get("/admin/tasks").catch(() => ({ data: [] })),
        api.get("/admin/projects").catch(() => ({ data: [] })),
        api.get("/admin/clients-lookup").catch(() => ({ data: { clients: [] } })),
        api.get("/admin/tenants").catch(() => ({ data: [] })),
      ]).then(([t, p, c, tn]) => {
        // /admin/tenants può restituire {tenants:[...]} o [...] a seconda della versione
        const tenants = Array.isArray(tn.data) ? tn.data : (tn.data?.tenants || tn.data?.items || []);
        setAdminIdx({
          tasks: Array.isArray(t.data) ? t.data : [],
          projects: Array.isArray(p.data) ? p.data : [],
          contacts: (c.data && c.data.clients) || [],
          tenants,
        });
      });
    } else {
      const already = tenantIdx.clients.length + tenantIdx.products.length + tenantIdx.services.length;
      if (already > 0) return;
      Promise.all([
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/services").catch(() => ({ data: [] })),
        api.get("/tags").catch(() => ({ data: [] })),
      ]).then(([c, p, s, t]) => {
        setTenantIdx({ clients: c.data, products: p.data, services: s.data, tags: t.data });
      });
    }
  }, [open, isAdmin]); // eslint-disable-line

  // Global shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onOpen);
    };
  }, [open]);

  const ADMIN_NAVS = useMemo(() => ([
    { kind: "nav", id: "adm-dash", label: "Panoramica", icon: LayoutDashboard, to: "/admin" },
    { kind: "nav", id: "adm-tasks", label: "Task del team", icon: CircleCheck, to: "/admin/tasks" },
    { kind: "nav", id: "adm-projects", label: "Progetti task", icon: Folder, to: "/admin/tasks" },
    { kind: "nav", id: "adm-contacts", label: "Contatti agenzia", icon: UsersIcon, to: "/admin/clients" },
    { kind: "nav", id: "adm-portafogli", label: "Portafogli commerciali", icon: TrendingUp, to: "/admin/portafogli" },
    { kind: "nav", id: "adm-pipeline", label: "Pipeline commerciale", icon: GitBranch, to: "/admin/pipeline" },
    { kind: "nav", id: "adm-publish", label: "Pubblica social", icon: Share2, to: "/admin/publish" },
    { kind: "nav", id: "adm-email", label: "Email Marketing", icon: Mail, to: "/admin/email" },
    { kind: "nav", id: "adm-automations", label: "Automazioni", icon: Zap, to: "/admin/automations" },
    { kind: "nav", id: "adm-inbox", label: "Inbox", icon: InboxIcon, to: "/admin/inbox" },
    { kind: "nav", id: "adm-tenants", label: "Saloni clienti", icon: Store, to: "/admin/tenants" },
    { kind: "nav", id: "adm-compare", label: "Confronto saloni", icon: TrendingUp, to: "/admin/compare" },
    { kind: "nav", id: "adm-billing", label: "Fatturazione (Stripe)", icon: Receipt, to: "/admin/billing" },
    { kind: "nav", id: "adm-formazione", label: "Formazione (corsi)", icon: GraduationCap, to: "/admin/formazione" },
    { kind: "nav", id: "adm-broadcast", label: "Broadcast email", icon: Megaphone, to: "/admin/broadcast" },
  ]), []);

  const TENANT_NAVS = useMemo(() => ([
    { kind: "nav", id: "dash", label: "Dashboard", icon: LayoutDashboard, to: "/app" },
    { kind: "nav", id: "agenda", label: "Agenda", icon: Calendar, to: "/app/agenda" },
    { kind: "nav", id: "clients", label: "Clienti", icon: User, to: "/app/clients" },
    { kind: "nav", id: "pipeline", label: "Pipeline CRM", icon: GitBranch, to: "/app/pipeline" },
    { kind: "nav", id: "tags", label: "Tag", icon: TagIcon, to: "/app/tags" },
    { kind: "nav", id: "services", label: "Servizi & Percorsi", icon: Scissors, to: "/app/services" },
    { kind: "nav", id: "inventory", label: "Magazzino", icon: Package, to: "/app/inventory" },
    { kind: "nav", id: "invoices", label: "Incassi", icon: Receipt, to: "/app/invoices" },
    { kind: "nav", id: "costs", label: "Costi", icon: TrendingDown, to: "/app/costs" },
    { kind: "nav", id: "automations", label: "Automazioni", icon: Zap, to: "/app/automations" },
    { kind: "nav", id: "email", label: "Email Marketing", icon: Mail, to: "/app/email" },
  ]), []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    const NAVS = isAdmin ? ADMIN_NAVS : TENANT_NAVS;
    if (!term) return NAVS.slice(0, 8);
    const match = (h) => (h || "").toLowerCase().includes(term);
    const out = [];
    for (const n of NAVS) if (match(n.label)) out.push(n);

    if (isAdmin) {
      for (const t of adminIdx.tasks) if (match(t.title) || match(t.description) || match(t.notes) || (t.tags || []).some(match)) {
        out.push({ kind: "task", id: t.id, label: t.title, sub: t.status || "task", icon: CircleCheck, to: "/admin/tasks" });
        if (out.length > 18) break;
      }
      for (const p of adminIdx.projects) if (match(p.name) || match(p.description)) {
        out.push({ kind: "progetto", id: p.id, label: p.name, sub: p.department || "progetto", icon: Folder, to: "/admin/tasks" });
        if (out.length > 22) break;
      }
      for (const c of adminIdx.contacts) if (match(c.name) || match(c.email) || match(c.phone)) {
        out.push({ kind: "contatto", id: c.id, label: c.name, sub: c.email || c.phone || "", icon: UsersIcon, to: `/admin/clients/${c.id}` });
        if (out.length > 26) break;
      }
      for (const t of adminIdx.tenants) if (match(t.name) || match(t.owner_email)) {
        out.push({ kind: "salone", id: t.id, label: t.name, sub: t.owner_email || "", icon: Store, to: `/admin/tenants/${t.id}` });
        if (out.length > 30) break;
      }
    } else {
      for (const c of tenantIdx.clients) if (match(c.name) || match(c.email) || match(c.phone)) {
        out.push({ kind: "client", id: c.id, label: c.name, sub: c.phone || c.email || "", icon: User, to: `/app/clients/${c.id}` });
        if (out.length > 18) break;
      }
      for (const p of tenantIdx.products) if (match(p.name)) {
        out.push({ kind: "product", id: p.id, label: p.name, sub: `stock ${p.quantity ?? 0}`, icon: Package, to: "/app/inventory" });
        if (out.length > 22) break;
      }
      for (const s of tenantIdx.services) if (match(s.name)) {
        out.push({ kind: "service", id: s.id, label: s.name, sub: `€${s.price} · ${s.duration_min}m`, icon: Scissors, to: "/app/services" });
        if (out.length > 26) break;
      }
      for (const t of tenantIdx.tags) if (match(t.name)) {
        out.push({ kind: "tag", id: t.id, label: `#${t.name}`, sub: "tag", icon: TagIcon, to: "/app/tags" });
        if (out.length > 30) break;
      }
    }
    return out;
  }, [q, isAdmin, tenantIdx, adminIdx, ADMIN_NAVS, TENANT_NAVS]);

  useEffect(() => { setHighlight(0); }, [q, open]);

  const go = (r) => {
    setOpen(false);
    setQ("");
    nav(r.to);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[highlight];
      if (r) go(r);
    }
  };

  if (!open) return null;

  const placeholder = isAdmin
    ? "Cerca task, progetti, contatti agenzia, saloni, sezioni admin…"
    : "Cerca clienti, servizi, prodotti, sezioni…";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4" onClick={() => setOpen(false)} data-testid="command-palette">
      <div className="sp-card w-full max-w-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--sp-border)]">
          <Search className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={placeholder}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--sp-text-muted)]"
            data-testid="command-search"
          />
          {isAdmin && (
            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]">
              <Building2 className="h-2.5 w-2.5 inline -mt-0.5 mr-0.5" /> Admin
            </span>
          )}
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-[color:var(--sp-border)] text-[color:var(--sp-text-muted)] font-mono">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-[color:var(--sp-text-muted)]">Nessun risultato per "{q}"</div>
          ) : (
            results.map((r, i) => {
              const Icon = r.icon;
              return (
                <button
                  key={`${r.kind}-${r.id}`}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => go(r)}
                  className={`w-full text-left px-4 py-2 flex items-center gap-3 ${i === highlight ? "bg-[color:var(--sp-brand)]/10" : ""}`}
                  data-testid={`command-result-${r.kind}-${r.id}`}
                >
                  <Icon className="h-4 w-4 text-[color:var(--sp-text-muted)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{r.label}</div>
                    {r.sub && <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{r.sub}</div>}
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-muted)] font-bold uppercase">{r.kind}</span>
                  <ArrowRight className="h-3 w-3 text-[color:var(--sp-text-muted)]" />
                </button>
              );
            })
          )}
        </div>
        <div className="px-4 py-2 border-t border-[color:var(--sp-border)] text-[10px] text-[color:var(--sp-text-muted)] flex items-center justify-between">
          <span className="inline-flex items-center gap-1"><CommandIcon className="h-3 w-3" /> Suggerimento: Cmd/Ctrl+K per riaprire</span>
          <span>↑↓ per muoverti · Invio per aprire</span>
        </div>
      </div>
    </div>
  );
}
